import { useEffect, type RefObject } from 'react';

/**
 * Close a popover on click-away / Escape.
 *
 * The document `pointerdown` half is the obvious bit. The `blur` half is the
 * one that matters here: the slide preview is an IFRAME, so a click inside it
 * never reaches the parent document and the menu used to hang around over a
 * slide the user had already moved on from. A click into an iframe blurs the
 * parent window and makes that iframe the active element — which is how we
 * tell it apart from a plain app-switch (Cmd+Tab), where the menu should stay
 * put so it's still there when the user comes back.
 *
 * Pass an ARRAY when the popover is portalled (`AnchoredPop`) and the natural
 * dismissal ref is one of its ancestors: the popover is no longer a descendant
 * of that ancestor, so its own clicks would read as outside clicks. Listing
 * both the host and the popover restores exactly the containment the host had
 * before it was portalled.
 *
 * @param ref     ref (or refs) to treat as inside — a pointerdown within any of
 *                them is ignored
 * @param onClose called when the popover should dismiss
 * @param enabled false while the popover is closed (nothing to dismiss)
 */
export function useDismissMenu(
  ref: RefObject<HTMLElement | null> | RefObject<HTMLElement | null>[],
  onClose: () => void,
  enabled = true,
) {
  useEffect(() => {
    if (!enabled) return undefined;
    const refs = Array.isArray(ref) ? ref : [ref];
    function onDown(e: PointerEvent) {
      // `contains` wants a Node; a pointerdown's target can be any EventTarget
      // (it is only ever an element here, but the narrowing is free).
      const target = e.target instanceof Node ? e.target : null;
      if (!target || !refs.some((r) => r.current?.contains(target))) onClose();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    function onBlur() {
      // Deferred: activeElement isn't updated yet while blur is dispatching.
      setTimeout(() => {
        if (document.activeElement?.tagName === 'IFRAME') onClose();
      }, 0);
    }
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('blur', onBlur);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('blur', onBlur);
    };
  }, [ref, onClose, enabled]);
}
